import React, { useEffect, useState } from "react";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { faqApis } from "../../../Data/axios";
import ReusableTable from "../ReusableTable";
import { faW } from "@fortawesome/free-solid-svg-icons";
import FadeInSection from "../../../Components/FadeInSection";
import config from "../../../Data/AuthUser.js";
import DashboardFAQViewMore from "./DashboardFAQViewMore";
import DashboardFAQEdit from "./DashboardFAQEdit";

const DashboardFAQ = () => {
  const IMAGE_API_URL = config.apiImageUrl;
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedFaq, setSelectedFaq] = useState(null);
  const [faqs, setFaqs] = useState([]); // Initialize as an empty array
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [queries, setQueries] = useState({
    page: 1,
    per_page: 10,
  });
  const [filters, setFilters] = useState({
    status: "",
  });
  const [newFaq, setNewFaq] = useState({
    question: "",
    answer: "",
    status: 1, // Defaults to '1: active'
    created_by: "userId", // Replace with the actual user ID dynamically
  });

  const headers = [
    { key: "id", label: "ID", sortable: true },
    { key: "author", label: "Author", sortable: true },
    { key: "question", label: "Question", sortable: true },
    { key: "answer", label: "Answer", sortable: true },
    { key: "status", label: "Status", sortable: true },
  ];

  const data = faqs.map((faq) => ({
    id: faq.id,
    question: faq.question,
    answer: faq.answer,
    status: faq.status === 1 ? "Active" : "Inactive",
    author: (
      <div className="flex items-center gap-3">
        {faq && faq.image && (
          <img
            src={`${IMAGE_API_URL}${faq.image}`}
            alt={faq?.name || "Creator"}
            className="w-10 h-10 rounded-full object-cover"
          />
        )}
        <div>
          <div>{faq.name}</div>
          {faq && faq.name && (
            <div className="text-xs text-gray-500">{faq.role}</div>
          )}
        </div>
      </div>
    ),
    creator: {
      name: faq?.name || "Name",
      image: faq?.image || null,
      role: faq?.role || "Role",
      role_name: faq?.role_name || "Unknown",
    },
    rawData: faq // Store the complete faq object
  }));

  // Fetch FAQs from the API
  const fetchData = async (filterOverrides = null) => {
    try {
      // Use filterOverrides if provided, otherwise use the filters from state
      const currentFilters = filterOverrides || filters;

      // Combine queries and filters into a single parameters object
      const params = {
        ...queries,
        ...(currentFilters.status && { status: currentFilters.status }),
        ...(currentFilters.type && { type: currentFilters.type }),
      };

      console.log("Fetching with parameters:", params);
      const response = await faqApis.getDashboardFaqs(params);
      setFaqs(response?.data || []);
    } catch (err) {
      setError(err.message || "Failed to fetch FAQs");
      console.error("Error fetching FAQs:", err);
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setLoading(true);
    fetchData();
  };

  useEffect(() => {
    refreshData();
  }, []);

  useEffect(() => {
    console.log("Queries changed, fetching data with new queries:", queries);
    refreshData();
  }, [queries, filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    // console.log(`Filter changed: ${name} = ${value}`);
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const clearFilters = () => {
    console.log("Clearing filters");
    setFilters({
      status: "",
    });
  };

  const onPageChange = (newPage) => {
    setQueries((prev) => ({ ...prev, page: newPage }));
    // console.log("The current query is:", queries);
  };

  const onPerPageChange = (newPerPage) => {
    // console.log("Changing per page to:", newPerPage);
    setQueries((prev) => ({ ...prev, per_page: newPerPage }));
    // console.log("The current query is:", queries);
  };

  // Handle input changes for the modal form
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewFaq((prev) => ({ ...prev, [name]: value }));
  };

  // Handle form submission to add a new FAQ
  const handleAddFAQ = async () => {
    // Check if required fields are filled
    if (!newFaq.question.trim() || !newFaq.answer.trim()) {
      setError("Question and answer fields are required");
      return;
    }

    try {
      setLoading(true);
      await faqApis.add(newFaq);
      // Refresh the FAQ list
      await fetchData();
      // Reset form data
      setNewFaq({
        question: "",
        answer: "",
        status: 1,
        created_by: "userId",
      });
      setShowModal(false);
      setError(null); // Clear any previous errors
    } catch (err) {
      setError(err.message || "Failed to add new FAQ");
      console.error("Error adding FAQ:", err);
    } finally {
      setLoading(false);
    }
  };
  const handleTableAction = (action, rowData) => {
    console.log("Table action received:", action, rowData);
    
    if (action === 'view') {
      // Ensure we're getting the complete data
      const faqData = rowData.rawData || rowData;
      console.log("Opening view modal with data:", faqData);
      setSelectedFaq(faqData);
      setShowViewModal(true);
    } 
    else if (action === 'edit') {
      const faqData = rowData.rawData || rowData;
      console.log("Opening edit modal with data:", faqData);
      setSelectedFaq(faqData);
      setShowEditModal(true);
    } 
    else if (action === 'delete') {
      const faqId = rowData.id || (rowData.rawData && rowData.rawData.id);
      if (faqId) {
        handleDelete(faqId);
      } else {
        console.error("Could not find ID for deletion");
      }
    }
  };
  
  // Add this function to handle edit save
  const handleEditSave = async (updatedData) => {
    try {
      setLoading(true);
      await faqApis.updateById(selectedFaq.id, updatedData);
      setShowEditModal(false);
      await refreshData();
    } catch (error) {
      console.error("Failed to update FAQ:", error);
      setError("Failed to update FAQ: " + (error.message || "Unknown error"));
    } finally {
      setLoading(false);
    }
  };
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this FAQ?")) {
      try {
        setLoading(true);
        await faqApis.deleteById(id);
        await refreshData();
      } catch (error) {
        console.error("Failed to delete FAQ:", error);
        setError("Failed to delete FAQ: " + (error.message || "Unknown error"));
      } finally {
        setLoading(false);
      }
    }
  };

  if (loading) return <div className="text-center py-10">Loading...</div>;
  // if (error)
  //   return <div className="text-center py-10 text-red-500">{error}</div>;

  return (
    <div className="bg-gray-200 min-h-screen text-black">
      {/* Filter Bar - Full width, separate container */}
      <div className="w-full bg-white shadow-sm mb-6">
        <div className="container mx-auto py-3 px-4">
          <div className="flex flex-col md:flex-row gap-3 md:items-center">
            <div className="flex items-center">
              <label className="text-sm font-medium text-gray-500 mr-2">
                Status:
              </label>
              <select
                name="status"
                className="border rounded p-1.5 text-gray-800 text-sm"
                value={filters.status}
                onChange={handleFilterChange}
              >
                <option value="">All Status</option>
                <option value="1">Active</option>
                <option value="2">Inactive</option>
              </select>
            </div>
            <div className="flex items-center">
            <label className="text-sm font-medium text-gray-500 mr-2">From:</label>
            <input
              type="date"
              name="start_date"
              className="border rounded p-1.5 text-gray-800 text-sm"
            />
            </div>
            <div className="flex items-center">
              <label className="text-sm font-medium text-gray-500 mr-2">To:</label>
              <input
                type="date"
                name="end_date"
                className="border rounded p-1.5 text-gray-800 text-sm"
              />
            </div>
            

            <button
              type="button"
              className="ml-auto bg-gray-100 text-gray-600 px-3 py-1.5 rounded text-sm hover:bg-gray-200"
              onClick={clearFilters}
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center mb-4 md:mx-6 mt-1">
          <h1 className="text-2xl font-bold font-ubuntu">FAQs</h1>
          <button
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            onClick={() => setShowModal(true)}
          >
            Add FAQ
          </button>
        </div>

        <div className="h-[50vh]">
          <ReusableTable
            headers={headers}
            data={data}
            rowKey="id"
            defaultSortKey="id"
            tableClassName=""
            headerClassName="text-gray-700"
            rowClassName="hover:bg-gray-200"
            tableApis={faqApis}
            onTableUpdate={refreshData}
            onPageChange={onPageChange}
            onPerPageChange={onPerPageChange}
            queries={queries}
            type="faq"
            onAction={handleTableAction}
          />
        </div>

        {showModal && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
            <div className="w-[0vw] md:w-1/6"></div>
            <div className="bg-white p-6 rounded shadow-lg w-full md:w-[85vw] h-[88vh] flex flex-col">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 text-center">
                Add New FAQ
              </h2>
              <div className="border-t border-gray-200 mb-6"></div>

              {/* Display validation error */}
              {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                  {error}
                </div>
              )}

              <div className="flex flex-col space-y-8 flex-grow">
                {/* Flex layout for headers and inputs */}
                <div className="flex">
                  <p className="text-sm font-medium text-gray-500 w-1/3">
                    Question
                  </p>
                  <textarea
                    className="w-2/3 border rounded p-2 text-gray-800"
                    rows="3"
                    name="question"
                    placeholder="Question"
                    value={newFaq.question}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="flex">
                  <p className="text-sm font-medium text-gray-500 w-1/3">
                    Answer
                  </p>
                  <textarea
                    className="w-2/3 border rounded p-2 text-gray-800"
                    rows="5"
                    name="answer"
                    placeholder="Answer"
                    value={newFaq.answer}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-start space-x-4">
                <button
                  type="button"
                  className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg"
                  onClick={() => {
                    setShowModal(false);
                    setError(null); // Clear errors when closing
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg"
                  onClick={(e) => {
                    e.preventDefault();
                    handleAddFAQ();
                  }}
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        )}
        {showViewModal && selectedFaq && (
  <DashboardFAQViewMore 
    data={selectedFaq.rawData || selectedFaq} 
    onClose={() => setShowViewModal(false)} 
  />
)}

{/* Edit FAQ Modal */}
{showEditModal && selectedFaq && (
  <DashboardFAQEdit 
    data={selectedFaq.rawData || selectedFaq}
    onClose={() => setShowEditModal(false)}
    onSave={handleEditSave}
  />
)}
      </div>
    </div>
  );
};

export default DashboardFAQ;
